/* METODIKA PLÁNU — volby poradce (Nastavení → Metodika plánu)
 * ============================================================================
 * Majitel 19. 10. 2026 k F9: „jo udělej to… a pak v nastavení můžeme změnit..
 * víc dynamicky, ať to není konzervativní jenom, poradce si pak to tam
 * nastaví“.
 *
 *   · profil   — výchozí rizikový profil NOVÝCH plánů (kino „Jaký jste
 *                investor“ ho u klienta přepíše v `navrh.solver.profil`),
 *   · posunLet — „Dynamičtější“ alokace: sloupec tabulky Fichtner o +N let
 *                dál (víc akcií dřív), 0–5; 0 = tabulky beze změny,
 *   · kapitalRenty — „pravidlo4“ (kapitál = roční renta / 4 %) nebo
 *                „poLetech“ (součet portfolií po letech čerpání, s. 26).
 *
 * Profil poradce (`profile.metodika_planu`, backend models.MetodikaPlanuVolby)
 * má snake_case; plán nese camelCase v `navrh.metodikaVolby`. Volby se do
 * návrhu ZAPÍŠOU při jeho vzniku (`metodikaPlanu.novyNavrh`), takže pozdější
 * změna nastavení starý plán nezmění. Platí jen pro plán s razítkem
 * 2026-10-18 a novějším (`metodikaPlanu.metodikaDoSolveru`).
 *
 *   · sazbaHypoPct — výchozí úrok hypotéky plánu v % (majitel 28. 10. 2026:
 *                „úroky dej aktuální, ale poradci si můžou sami upravit“).
 *                Nový plán dostane aktuální tržní sazbu z config2026
 *                (`SAZBA_HYPO_TRH_PCT`), u cíle ji poradce přepíše
 *                (`cil.sazbaHypoPct`). Plán uložený bez ní počítá dál 4,9 %
 *                (`planSolver.SAZBA_HYPO`) — čísla, která klient viděl, se nemění.
 *
 *   · vstupniPoplatekPct, poplatekZpusob — vstupní poplatek investičních cílů
 *                Návrhu (majitel 28. 10. 2026: „vstupní poplatek, od něco do
 *                4,5 % z cílové částky… 60/40, 100 % expres“). Nový plán dostane
 *                2 % a 60/40; plán uložený bez nich počítá 0 % (čísla, která
 *                klient viděl, se nemění). U cíle je poradce přepíše
 *                (`kasickaBeta.cile[id].poplatek`, lib/navrhBeta/poplatekCile.js).
 *
 * Modul je BEZ ZÁVISLOSTÍ mimo data/config2026 (taky bez závislostí; čte ho
 * metodikaPlanu → planSolver i holý node).
 */
import { CONFIG_2026 } from "../data/config2026";

/** Aktuální tržní sazba hypotéky v % (Hypoindex, config2026). */
export const SAZBA_HYPO_TRH_PCT = CONFIG_2026.DEFAULTS.HYPO_SAZBA;
const SAZBA_MAX_PCT = 25; // strop proti překlepu, jako planSolver.SAZBA_HYPO_MAX_PCT

/* Vstupní poplatek cílů Návrhu (28. 10. 2026): výchozí nového plánu, strop 4,5 %
 * (stejný jako VN analýza `izp/vypocty.POPLATEK_PCT_MAX`, hlídá test). */
export const POPLATEK_VYCHOZI_PCT = 2;
export const POPLATEK_STROP_PCT = 4.5;
export const ZPUSOBY_POPLATKU = ["60/40", "expres", "hned"];
export const POPLATEK_ZPUSOB_VYCHOZI = "60/40";

/** Poplatek v % (0 ≤ x ≤ 4,5), jinak `null` = nezadáno (prázdno, text, NaN, záporné, nad strop). */
export function cistyPoplatekPct(v) {
  if (v === null || v === undefined || v === "" || typeof v === "boolean") return null;
  const n = +v;
  return Number.isFinite(n) && n >= 0 && n <= POPLATEK_STROP_PCT ? n : null;
}

/** Způsob placení poplatku, jinak `null`. */
export const cistyZpusobPoplatku = (v) => (ZPUSOBY_POPLATKU.includes(v) ? v : null);

export const PROFILY_VOLBY = ["konzervativni", "vyvazeny", "dynamicky"];
export const KAPITAL_RENTY = ["pravidlo4", "poLetech"];
export const POSUN_MAX = 5;

/** Výchozí volby = tabulky Fichtner bez posunu, kapitál pravidlem 4 %. */
export const VOLBY_VYCHOZI = Object.freeze({ profil: "vyvazeny", posunLet: 0, kapitalRenty: "pravidlo4" });

const jeObjekt = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

/** Posun 0–5 celé číslo; NaN, text, prázdno, záporné → 0; nad 5 → 5. */
export function cistyPosun(v) {
  if (v === null || v === undefined || v === "" || typeof v === "boolean") return 0;
  const n = Math.round(+v);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(POSUN_MAX, n);
}

/** Sazba v % (0 < x ≤ 25), jinak `null` = nezadáno (prázdno, text, NaN, 0, záporné). */
export function cistaSazba(v) {
  if (v === null || v === undefined || v === "" || typeof v === "boolean") return null;
  const n = +v;
  return Number.isFinite(n) && n > 0 && n <= SAZBA_MAX_PCT ? n : null;
}

/** Vyčištěné volby z libovolného objektu v camelCase (`navrh.metodikaVolby`). */
export function cisteVolby(o) {
  const v = jeObjekt(o) ? o : {};
  const out = {
    profil: PROFILY_VOLBY.includes(v.profil) ? v.profil : VOLBY_VYCHOZI.profil,
    posunLet: cistyPosun(v.posunLet),
    kapitalRenty: KAPITAL_RENTY.includes(v.kapitalRenty) ? v.kapitalRenty : VOLBY_VYCHOZI.kapitalRenty,
  };
  const sazba = cistaSazba(v.sazbaHypoPct);
  if (sazba !== null) out.sazbaHypoPct = sazba;
  const poplatek = cistyPoplatekPct(v.vstupniPoplatekPct);
  if (poplatek !== null) out.vstupniPoplatekPct = poplatek;
  const zpusob = cistyZpusobPoplatku(v.poplatekZpusob);
  if (zpusob !== null) out.poplatekZpusob = zpusob;
  return out;
}

/** Volby z profilu poradce (`profile.metodika_planu`, snake_case). „Tabulky“ = posun 0. */
export function volbyZProfilu(profil) {
  const m = jeObjekt(profil) && jeObjekt(profil.metodika_planu) ? profil.metodika_planu : {};
  return cisteVolby({
    profil: m.profil,
    posunLet: m.alokace === "dynamictejsi" ? m.posun_let : 0,
    kapitalRenty: m.kapital_renty,
  });
}

/** Volby → tvar pro PUT /api/profile/me (`metodika_planu`). */
export function volbyDoProfilu(volby) {
  const v = cisteVolby(volby);
  return { profil: v.profil, alokace: v.posunLet > 0 ? "dynamictejsi" : "tabulky", posun_let: v.posunLet, kapital_renty: v.kapitalRenty };
}

/** Volby uložené v návrhu (`navrh.metodikaVolby`); bez nich výchozí. */
export function volbyNavrhu(navrh) {
  return cisteVolby(jeObjekt(navrh) ? navrh.metodikaVolby : null);
}

/**
 * Výchozí úrok hypotéky plánu v %: návrh, který ještě neexistuje = aktuální
 * tržní; uložený návrh = sazba zapsaná při jeho vzniku, bez ní `null`
 * (volající počítá původních 4,9 %).
 */
export function sazbaHypoNavrhuPct(navrh) {
  if (!jeObjekt(navrh)) return SAZBA_HYPO_TRH_PCT;
  return volbyNavrhu(navrh).sazbaHypoPct ?? null;
}

/**
 * Výchozí vstupní poplatek cílů plánu: návrh, který ještě neexistuje = nový
 * (2 %, 60/40); uložený návrh = co bylo zapsané při jeho vzniku, bez toho 0 %.
 * @returns {{pct: number, zpusob: string}}
 */
export function poplatekNavrhu(navrh) {
  if (!jeObjekt(navrh)) return { pct: POPLATEK_VYCHOZI_PCT, zpusob: POPLATEK_ZPUSOB_VYCHOZI };
  const v = volbyNavrhu(navrh);
  return { pct: v.vstupniPoplatekPct ?? 0, zpusob: v.poplatekZpusob ?? POPLATEK_ZPUSOB_VYCHOZI };
}

/** Volby pro solver — jen to, co se liší od výchozího (prázdný objekt = beze změny). */
export function volbyDoSolveru(navrh) {
  const v = volbyNavrhu(navrh);
  const out = {};
  if (v.posunLet > 0) out.posunLet = v.posunLet;
  if (v.kapitalRenty !== VOLBY_VYCHOZI.kapitalRenty) out.kapitalRenty = v.kapitalRenty;
  if (v.sazbaHypoPct !== undefined) out.sazbaHypo = v.sazbaHypoPct / 100;
  return out;
}
